import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti del Card
 * AGGIORNATO: Usa variabili tema
 */
const cardVariants = cva(
  'rounded-lg transition-all duration-200',
  {
    variants: {
      variant: {
        default: 'bg-base-100 border border-base-300 shadow-sm',
        outlined: 'bg-base-100 border-2 border-base-300',
        elevated: 'bg-base-100 shadow-lg hover:shadow-xl',
      },
      padding: {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
    },
  }
);

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    BaseComponentProps,
    CVAVariantProps<typeof cardVariants> {
  /**
   * Usa tag semantico article invece di div
   */
  asArticle?: boolean;
  /**
   * Hover effect (solleva la card)
   */
  hover?: boolean;
  /**
   * @deprecated Use 'hover' instead
   */
  hoverable?: boolean;
}

/**
 * Componente Card
 * 
 * Container versatile per contenuti con varianti di stile.
 * Supporta tag semantici HTML5 per migliore accessibilità.
 * Ottimizzato con React.memo per prevenire re-render non necessari.
 * 
 * @example
 * ```tsx
 * <Card variant="elevated" hover>
 *   <CardHeader>Title</CardHeader>
 *   <CardBody>Content</CardBody>
 *   <CardFooter>Footer</CardFooter>
 * </Card>
 * ```
 */
const CardComponent = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant,
      padding,
      asArticle,
      hover,
      hoverable,
      children,
      ...props
    },
    ref
  ) => {
    const Component = asArticle ? 'article' : 'div';
    const shouldHover = hover ?? hoverable;

    return (
      <Component
        ref={ref}
        className={cn(
          cardVariants({ variant, padding }),
          shouldHover && 'hover:shadow-lg hover:scale-[1.02] cursor-pointer transition-transform',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardComponent.displayName = 'Card';

export const Card = memo(CardComponent);

/**
 * Card Header
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement>, BaseComponentProps {}

const CardHeaderComponent = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mb-4', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeaderComponent.displayName = 'CardHeader';

export const CardHeader = memo(CardHeaderComponent);

/**
 * Card Body
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement>, BaseComponentProps {}

const CardBodyComponent = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardBodyComponent.displayName = 'CardBody';

export const CardBody = memo(CardBodyComponent);

/**
 * Card Footer
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement>, BaseComponentProps {}

const CardFooterComponent = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mt-4 pt-4 border-t border-base-300', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooterComponent.displayName = 'CardFooter';

export const CardFooter = memo(CardFooterComponent);

